import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  useTheme,
  alpha,
  Badge,
  CircularProgress,
} from "@mui/material";
import { getSaleProductById } from "../../../services/Sales";
import TableItem from "../../../components/TableItem";
import ProfitCard from "../../../components/ProfitCard";
import CustomerDataComponent from "../../../components/CustomerDataComponent";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import MoneyIcon from "@mui/icons-material/Money";
import DescriptionIcon from "@mui/icons-material/Description";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";

function SaleProductView() {
  const theme = useTheme();
  const history = useHistory();
  const { saleId } = useParams();
  const [sale, setSale] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getSaleProductById(saleId);

      if (result && result.saleId) {
        setSale(result);

        // Process production batches
        if (result.productionBatches && result.productionBatches.length > 0) {
          const batches = result.productionBatches.map((batch) => ({
            batchNumber: batch.production.productionId.toString(),
            productionDate: formatDate(batch.production.productionDate),
            totalItemCost: batch.production.totalItemCost,
            labourCost: batch.production.labourCost,
            electricityCost: batch.production.electricityCost,
            transportCost: batch.production.transportCost,
            otherCost: batch.production.otherCost,
            saleQty: batch.quantity,
            // Calculate total cost
            totalCost: calculateTotalCost(batch.production),
          }));

          setRows(batches);
          setTotalPages(Math.ceil(batches.length / 10));
        } else {
          setRows([]);
          setTotalPages(0);
        }
      }
    } catch (error) {
      console.error("Error fetching sale data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [saleId]);

  const calculateTotalCost = (production) => {
    return (
      parseFloat(production.totalItemCost || 0) +
      parseFloat(production.labourCost || 0) +
      parseFloat(production.electricityCost || 0) +
      parseFloat(production.transportCost || 0) +
      parseFloat(production.otherCost || 0)
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "$0.00";

    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  // Table columns with improved formatting
  const columns = [
    {
      id: "batchNumber",
      label: "Batch Number",
      minWidth: 80,
      align: "center",
    },
    {
      id: "productionDate",
      label: "Production Date",
      minWidth: 100,
    },
    {
      id: "totalItemCost",
      label: "Material Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "labourCost",
      label: "Labour Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "electricityCost",
      label: "Electricity Cost",
      minWidth: 120,
      format: (value) => formatCurrency(value),
    },
    {
      id: "transportCost",
      label: "Transport Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: "otherCost",
      label: "Other Cost",
      minWidth: 90,
      format: (value) => formatCurrency(value),
    },
    {
      id: "saleQty",
      label: "Sold Qty",
      minWidth: 80,
      align: "center",
    },
    {
      id: "totalCost",
      label: "Total Cost",
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading product details...
        </Typography>
      </Box>
    );
  }

  if (!sale || !sale.saleId) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No product data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ShoppingCartIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Sale Product Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Product Information */}
      <Grid container spacing={2}>
        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Product Name
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {sale.product.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Barcode
                  </Typography>
                  <Typography variant="body1">
                    {sale.product.barcode}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Existing Qty
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Chip
                      label={sale.product.existingQty}
                      color={
                        parseInt(sale.product.existingQty) > 10
                          ? "success"
                          : "warning"
                      }
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">{sale.addedBy}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Profit and Pricing */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Pricing Information
                </Typography>
              </Box>

              {/* Use the existing ProfitCard component */}
              <Box sx={{ mb: 2 }}>
                <ProfitCard
                  totalPrice={sale.totalPrice}
                  profit={sale.totalProfit}
                  profitMargin={sale.profitMargin}
                />
              </Box>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Price Per Unit
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="primary.main"
                    >
                      {formatCurrency(sale.sellingPricePerUnit)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Cost Per Unit
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="text.secondary"
                    >
                      {formatCurrency(sale.costPerProduct)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Price
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="success.main"
                    >
                      {formatCurrency(sale.totalPrice)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Selling Quantity
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="info.main"
                    >
                      {sale.sellingQty}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Categories */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Categories
                </Typography>
              </Box>

              {sale.product?.ex_categories?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {sale.product.ex_categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {category.category}
                          </span>
                          : {category.subCat_1}
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No categories available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bill Info */}
        {sale.billNumber && (
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ReceiptIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Bill Information
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ReceiptIcon
                    sx={{
                      fontSize: 40,
                      mr: 2,
                      color: alpha(theme.palette.info.main, 0.7),
                    }}
                  />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Bill Number
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="info.main">
                      {sale.billNumber}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Customer Info */}
        <Grid item xs={12} md={sale.billNumber ? 6 : 12}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Customer Information
                </Typography>
              </Box>

              {sale.customer ? (
                <CustomerDataComponent customer={sale.customer} />
              ) : (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    No customer data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Production Batches Table */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ProductionQuantityLimitsIcon
                  sx={{ mr: 1, color: "primary.main" }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Production Batches
                </Typography>
                <Badge
                  badgeContent={rows.length}
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Box>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <TableItem
                  columns={columns}
                  rows={rows}
                  page={page}
                  setPage={setPage}
                  tablePagin={true}
                  totalPages={totalPages}
                  getData={fetchData}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Note */}
        {sale.note && (
          <Grid item xs={12}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Note
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
                  {sale.note}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

SaleProductView.propTypes = {
  saleId: PropTypes.string,
};

export default SaleProductView;
